import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Box, GridLegacy as Grid, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { gql } from "generated-graphql";
import { LeadAgencyInput } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useMemo, useCallback } from "react";
import { FormProvider } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";

import { GET_ALL_NJ_LEAD_AGENECIES, GET_NJ_LEAD_AGENECY, UPSERT_LEAD_AGENCY } from "./schema";

const VALIDATE_LEAD_AGENCY_INPUT = gql(`
  query ValidateLeadAgencylInput($id: Int, $data: LeadAgencyInput!) {
    leadAgencyInputValidator(id: $id, data: $data) {
      ...issue
    }
  }
`);

export function useLeadAgencyInputValidation(isCreate: boolean) {
  const [query] = useLazyQuery(VALIDATE_LEAD_AGENCY_INPUT, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (input: LeadAgencyInput) => {
      const safeInput = {
        ...input,
        name: input.name ?? "",
        address: input.address ?? "",
        attn: input.attn ?? "",
        city: input.city ?? "",
        consolidated: input.consolidated ?? "",
        phone: input.phone ?? "",
        state: input.state ?? "",
        zip: input.zip ?? "",
        email: input.email ?? "",
        additionalComments: input.additionalComments ?? "",
      };
      const { data } = await query({
        variables: {
          data: safeInput,
          id: !isCreate ? input.id : undefined,
        },
      });
      return data?.leadAgencyInputValidator ?? [];
    },
    [query, isCreate]
  );
}

type LeadAngencyDialogProps = {
  agencyType: string;
  onClose: () => void;
  mode?: "add" | "edit";
  agencyId?: number;
};

export const LeadAgencyDialog: React.FC<LeadAngencyDialogProps> = ({
  agencyType,
  onClose,
  mode,
  agencyId,
}) => {
  const theme = useTheme();
  const alerts = useAlerts();

  const { data: agency, loading: isLoadingAgency } = useQuery(GET_NJ_LEAD_AGENECY, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: agencyId ?? -1,
    },
    skip: !agencyId,
  });

  const [upsertLeadAgency, { loading: isUpserting }] = useMutation(UPSERT_LEAD_AGENCY, {
    refetchQueries: ["LeadAgency"],
  });

  const defaultValues: LeadAgencyInput = useMemo(
    () => ({
      id: agency?.leadAgency.id ?? undefined,
      name: agency?.leadAgency?.name ?? "",
      address: agency?.leadAgency?.address ?? "",
      attn: agency?.leadAgency?.attn ?? "",
      city: agency?.leadAgency?.city ?? "",
      consolidated: agency?.leadAgency?.consolidated ?? "",
      phone: agency?.leadAgency?.phone ?? "",
      state: agency?.leadAgency?.state ?? "NJ",
      zip: agency?.leadAgency?.zip ?? "",
      email: agency?.leadAgency?.email ?? "",
      additionalComments: agency?.leadAgency?.additionalComments ?? "",
    }),
    [agency]
  );

  const form = useValidatingForm<LeadAgencyInput>(
    defaultValues,
    agency?.leadAgency.issues,
    useLeadAgencyInputValidation(mode === "add")
  );

  const { handleSubmit, control, getValues, issues } = form;

  const onSubmit = async () => {
    const input = {
      ...getValues(),
    };

    await upsertLeadAgency({
      variables: { id: agencyId, input },
      onCompleted: () => {
        onClose();
        alerts.success("Agency saved successfully");
      },
      onError: (error) => {
        console.error(error);
        alerts.error("An error occurred while saving the Agency");
      },
      refetchQueries: [GET_ALL_NJ_LEAD_AGENECIES],
    });
  };

  const savingOrLoading = isLoadingAgency || isUpserting;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      title={`${mode === "add" ? "Add" : "Edit"} ${agencyType}`}
      primaryActionButtonProps={{
        loading: savingOrLoading,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !form.formState.isDirty,
      }}
    >
      <FormProvider {...form}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            alignItems: "center",
            padding: 0.5,
            width: 500,
          }}
        >
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={3} minWidth="100%">
              <FormTextField
                name="name"
                label="Name"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="address"
                label="Address"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="attn"
                label="Attn"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="city"
                label="City"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="state"
                label="State"
                control={control}
                disabled
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="zip"
                label="Zip"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="consolidated"
                label="Consolidated"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField
                name="phone"
                label="Phone"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField name="email" label="Email" control={control} />
              <FormTextField
                name="additionalComments"
                label="Additional Comments"
                control={control}
                textFieldProps={{ multiline: true, minRows: 2 }}
              />
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </Dialog>
  );
};
